import React from "react";
import { AppBadge } from "../ui";

/**
 * Canonical lead-source metadata. Keyed by the `leadSource` enum stored on the
 * Enquiry model (models/Enquiry.js).
 */
const SOURCE_META = {
  website:    { label: "Website",      color: "blue"    },
  embed_form: { label: "Enquiry Form", color: "violet"  },
  whatsapp:   { label: "WhatsApp",     color: "emerald" },
  google_ads: { label: "Google Ads",   color: "amber"   },
  meta_ads:   { label: "Meta Ads",     color: "rose"    },
  zoho_crm:   { label: "Zoho CRM",      color: "primary" },
  manual:     { label: "Manual",        color: null      },
  other:      { label: "Other",         color: null      },
};

/**
 * Resolve where an enquiry came from into a stable, display-friendly shape.
 *
 * Older enquiries (created before the `leadSource` enum existed) only carry the
 * free-text `source` string, so we derive the channel from that when the enum
 * is missing. This is what lets an admin tell a Website (vc_cms) lead apart from
 * an Enquiry-Form submission apart from a manually-added record.
 *
 * @returns {{ key: string, label: string, color: string|null, variant?: string, detail: string|null }}
 */
export function resolveEnquirySource(e = {}) {
  const ls = e.leadSource;
  if (ls && SOURCE_META[ls]) {
    return { key: ls, ...SOURCE_META[ls], detail: e.source || null };
  }

  const src = (e.source || "").trim();
  const lower = src.toLowerCase();

  // Embed / enquiry form — stored as "Enquiry Form: <form name>".
  if (/^enquiry form\b/i.test(src)) {
    const name = src.replace(/^enquiry form:?\s*/i, "").trim();
    return { key: "embed_form", ...SOURCE_META.embed_form, detail: name || null };
  }
  // Marketing website (vc_cms) — contact form, book-a-demo, demo-class sync, etc.
  if (
    lower.startsWith("cms") ||
    lower === "website" ||
    lower.includes("demo class") ||
    lower.includes("book-demo") ||
    lower.includes("book demo")
  ) {
    return { key: "website", ...SOURCE_META.website, detail: src || null };
  }
  if (lower.includes("zoho")) return { key: "zoho_crm", ...SOURCE_META.zoho_crm, detail: src };
  if (lower.includes("whatsapp")) return { key: "whatsapp", ...SOURCE_META.whatsapp, detail: src };
  if (lower.includes("google")) return { key: "google_ads", ...SOURCE_META.google_ads, detail: src };
  if (lower.includes("meta") || lower.includes("facebook") || lower.includes("instagram")) {
    return { key: "meta_ads", ...SOURCE_META.meta_ads, detail: src };
  }

  // Unknown-but-present source string → show it verbatim.
  if (src) return { key: "other", label: src, color: null, variant: "outline", detail: null };

  // No leadSource and no source string → an admin created it by hand.
  return { key: "manual", ...SOURCE_META.manual, detail: null };
}

/**
 * Small coloured badge that names the channel an enquiry arrived through.
 * Hover shows the raw source detail (e.g. the form or campaign name).
 *
 * ⚠️ It carried `className="eq-source-badge"`, which is ALSO the class the
 * enquiry DETAIL page draws its own `<span>` with. Both that rule and
 * `.app-badge` are (0,1,0) and enquiry-view.scss is bundled later, so the
 * detail page's flat grey pill won here: every badge in the list came out the
 * same colour at 0.75rem/500, and `s.color` — the whole point of this
 * component — reached nothing. That rule is scoped to `.eq-view-page` now, and
 * this needs no class of its own.
 */
export function EnquirySourceBadge({ enquiry }) {
  const s = resolveEnquirySource(enquiry);
  const title = s.detail ? `${s.label} · ${s.detail}` : s.label;
  return (
    <AppBadge
      variant={s.variant || "secondary"}
      color={s.color || undefined}
      size="sm"
      title={title}
    >
      {s.label}
    </AppBadge>
  );
}

export default resolveEnquirySource;
